import React, { useState, useEffect } from 'react';
import Button from '@dashboard/components/Button';
import { Textarea } from '@dashboard/components/ui/textarea';
import { format } from 'date-fns';
import { toast } from '@dashboard/context/ToastContext';
import { Badge } from '@dashboard/components/ui/badge';
import { useAdminBrandLog } from '@dashboard/hooks/queries/useAdminQueries';
import {
  useTestBrandExtraction,
  useClearBrandCache,
} from '@dashboard/hooks/queries/useAdminMutations';
import { ArrowPathIcon } from '@heroicons/react/24/outline';

function parseInputs(text) {
  return text
    .split(/[\n,\s]+/)
    .map(s => s.trim())
    .filter(Boolean);
}

function StatusBadge({ status }) {
  const variants = {
    pending: 'warning',
    completed: 'success',
    failed: 'destructive',
  };
  return (
    <Badge variant={variants[status] || 'secondary'} className="first-letter:uppercase">
      {status}
    </Badge>
  );
}

function ColorSwatch({ color }) {
  if (!color) return <span className="text-gray-400 dark:text-muted-foreground">-</span>;
  return (
    <div className="flex items-center gap-2">
      <div
        className="h-5 w-5 rounded border border-gray-300 dark:border-border"
        style={{ backgroundColor: color }}
        title={color}
      />
      <span className="text-sm text-gray-600 dark:text-muted-foreground">{color}</span>
    </div>
  );
}

function LogoPreview({ logo, rawLogoUrl }) {
  const url = logo?.url || rawLogoUrl;
  if (!url) return <span className="text-gray-400 dark:text-muted-foreground">-</span>;
  return (
    <img
      src={url}
      alt="Logo"
      className="h-8 w-8 object-contain rounded border border-gray-200 dark:border-border"
      onError={e => {
        e.target.style.display = 'none';
      }}
    />
  );
}

export default function BrandFetches() {
  const [testInput, setTestInput] = useState('');
  const [pendingDomains, setPendingDomains] = useState([]);

  const shouldPoll = pendingDomains.length > 0;
  const { data, isLoading, refetch } = useAdminBrandLog({
    refetchInterval: shouldPoll ? 2000 : false,
  });
  const entries = data || [];

  useEffect(() => {
    if (pendingDomains.length === 0) return;

    const stillPending = pendingDomains.filter(domain => {
      const entry = entries.find(e => e.domain === domain);
      return !entry || entry.status === 'pending';
    });

    if (stillPending.length !== pendingDomains.length) {
      setPendingDomains(stillPending);

      if (stillPending.length === 0) {
        const completed = pendingDomains.filter(domain => {
          const entry = entries.find(e => e.domain === domain);
          return entry?.status === 'completed';
        });
        const failed = pendingDomains.length - completed.length;

        toast({
          title: `Processed ${pendingDomains.length} domain${pendingDomains.length !== 1 ? 's' : ''}`,
          description: `${completed.length} succeeded${failed > 0 ? `, ${failed} failed` : ''}`,
          type: failed === pendingDomains.length ? 'error' : failed > 0 ? 'warning' : 'success',
        });
      }
    }
  }, [entries, pendingDomains, toast]);

  const testMutation = useTestBrandExtraction({
    onSuccess: response => {
      const { results, domains } = response;
      const queued = results.filter(r => r.queued).length;
      const alreadyCached = results.filter(r => r.reason === 'already_cached').length;
      const invalid = results.filter(r => r.reason === 'invalid_input').length;

      const domainsToTrack = results.filter(r => r.queued).map(r => r.domain);

      if (domainsToTrack.length > 0) {
        setPendingDomains(domainsToTrack);
      }

      const messages = [];
      if (queued > 0) messages.push(`${queued} queued`);
      if (alreadyCached > 0) messages.push(`${alreadyCached} already cached`);
      if (invalid > 0) messages.push(`${invalid} invalid`);

      toast({
        title: `Queued ${domains.length} domain${domains.length !== 1 ? 's' : ''}`,
        description: messages.join(', ') || 'Processing...',
        type: invalid === results.length ? 'error' : 'success',
      });
      setTestInput('');
    },
    onError: error => {
      toast({
        title: 'Error testing brand',
        description: error?.message,
        type: 'error',
      });
    },
  });

  const clearCacheMutation = useClearBrandCache({
    onSuccess: (_result, { domain }) => {
      toast({
        title: 'Cache cleared',
        description: `Cleared cache for ${domain}`,
        type: 'success',
      });
    },
    onError: (error, { domain }) => {
      toast({
        title: `Error clearing cache for ${domain}`,
        description: error?.message,
        type: 'error',
      });
    },
  });

  const handleTest = e => {
    e.preventDefault();
    const inputs = parseInputs(testInput);
    if (inputs.length === 0) return;
    testMutation.mutate({ inputs });
  };

  return (
    <div className="">
      <div className="mb-6">
        <h1>Brand fetches</h1>
        <p className="text-gray-600 dark:text-muted-foreground mt-1">
          Test brand extraction and view cached brand data
        </p>
      </div>

      <div className="mb-8">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground">
          Test brand extraction
        </h2>
        <p className="text-sm text-gray-500 dark:text-muted-foreground mt-1 mb-3">
          Enter domains or email addresses to extract brand logos, colors, and names.
        </p>
        <div>
          <form onSubmit={handleTest}>
            <label className="block text-sm font-medium text-gray-700 dark:text-muted-foreground mb-1">
              Domains or emails (one per line, or separated by commas/spaces)
            </label>
            <Textarea
              value={testInput}
              onChange={e => setTestInput(e.target.value)}
              placeholder="checkoutpage.com or user@checkoutpage.com"
              disabled={testMutation.isPending}
              rows={3}
            />
            <div className="mt-3 flex items-center justify-end gap-3">
              {pendingDomains.length > 0 && (
                <span className="text-sm text-gray-500 dark:text-muted-foreground flex items-center gap-2">
                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                  Processing {pendingDomains.length} domain{pendingDomains.length !== 1 ? 's' : ''}
                  ...
                </span>
              )}
              <Button
                type="submit"
                appearance="primary"
                disabled={parseInputs(testInput).length === 0 || testMutation.isPending}
                isLoading={testMutation.isPending}
              >
                Test
              </Button>
            </div>
          </form>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-foreground">
              Cached brand log
              <span className="ml-2 text-sm font-normal text-gray-500 dark:text-muted-foreground">
                ({entries.length})
              </span>
            </h2>
            <p className="text-sm text-gray-500 dark:text-muted-foreground mt-1">
              Brand data is cached for 1 hour to reduce API calls.
            </p>
          </div>
          <Button appearance="light" size="small" onClick={() => refetch()} disabled={isLoading}>
            <ArrowPathIcon className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        {isLoading && entries.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-muted-foreground">
            Loading...
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-muted-foreground">
            No cached brand data found. Use the test form above to fetch brands.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-border">
              <thead className="bg-gray-50 dark:bg-card">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
                    Domain
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
                    Logo
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
                    Color
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
                    Fetched at
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-muted-foreground uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-background divide-y divide-gray-200 dark:divide-border">
                {entries.map(entry => (
                  <tr key={entry.domain}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-foreground">
                        {entry.domain}
                      </div>
                      {entry.error && (
                        <div className="text-xs text-red-500 truncate max-w-xs" title={entry.error}>
                          {entry.error}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <LogoPreview logo={entry.result?.logo} rawLogoUrl={entry.rawLogoUrl} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm text-gray-900 dark:text-foreground">
                        {entry.result?.name || (
                          <span className="text-gray-400 dark:text-muted-foreground">-</span>
                        )}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <ColorSwatch color={entry.result?.brandColor} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <StatusBadge status={entry.status} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-muted-foreground">
                      {entry.fetchedAt
                        ? format(new Date(entry.fetchedAt), 'yyyy-MM-dd HH:mm:ss')
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Button
                        appearance="light"
                        size="small"
                        onClick={() => clearCacheMutation.mutate({ domain: entry.domain })}
                        disabled={clearCacheMutation.isPending}
                      >
                        Clear cache
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
